import { useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loader } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Send, MailWarning, QrCode, ExternalLink, Copy, Users, Eye, History } from "lucide-react";
import { fdateShort } from "@/lib/format";

export default function RsvpBuilder() {
  const { wedding } = useAuth();
  const wid = wedding?.wedding?.id;
  const [form, setForm] = useState({ title: "Confirmez votre présence", intro: "", deadline: "", questions: [], published: false });
  const [guests, setGuests] = useState([]);
  const [households, setHouseholds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("config"); // config | preview | history
  const [preview, setPreview] = useState(null);
  const [sampleId, setSampleId] = useState("");
  const [campaigns, setCampaigns] = useState([]);
  const [sendModal, setSendModal] = useState(null); // {breakdown, resend_all}
  const [remindModal, setRemindModal] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/weddings/${wid}/rsvp-form`);
    if (data.form) setForm({ ...form, ...data.form });
    const g = await api.get(`/weddings/${wid}/guests`); setGuests(g.data);
    const h = await api.get(`/weddings/${wid}/households`); setHouseholds(h.data);
    setLoading(false);
  };
  useEffect(() => { if (wid) load(); }, [wid]);

  const loadPreview = async (gid) => {
    const { data } = await api.get(`/weddings/${wid}/rsvp/email-preview${gid ? `?guest_id=${gid}` : ""}`);
    setPreview(data);
  };
  const loadCampaigns = async () => { const { data } = await api.get(`/weddings/${wid}/rsvp/campaigns`); setCampaigns(data); };
  useEffect(() => { if (wid && tab === "preview" && !preview) loadPreview(sampleId); }, [tab, wid]);
  useEffect(() => { if (wid && tab === "history") loadCampaigns(); }, [tab, wid]);

  const save = async () => { await api.put(`/weddings/${wid}/rsvp-form`, form); toast.success("Formulaire enregistré"); };
  const publish = async () => { const nf = { ...form, published: true }; setForm(nf); await api.put(`/weddings/${wid}/rsvp-form`, nf); toast.success("RSVP publié"); };
  const addQ = (preset) => setForm({ ...form, questions: [...form.questions, preset || { label: "", type: "text", required: false, enabled: true, options: [] }] });
  const updQ = (i, k, v) => { const q = [...form.questions]; q[i][k] = v; setForm({ ...form, questions: q }); };
  const delQ = (i) => setForm({ ...form, questions: form.questions.filter((_, x) => x !== i) });

  const SUGGESTIONS = [
    { label: "Choix du menu", type: "meal", options: ["Viande", "Poisson", "Végétarien", "Vegan", "Menu enfant"] },
    { label: "Allergies / régime", type: "allergies" },
    { label: "Besoin d'une navette ?", type: "yesno" },
    { label: "Présent au brunch du dimanche ?", type: "yesno" },
    { label: "Chanson à passer", type: "text" },
  ];

  const openSend = async (resend_all) => {
    const { data } = await api.get(`/weddings/${wid}/rsvp/send-breakdown`);
    setSendModal({ breakdown: data, resend_all });
  };
  const confirmSend = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/weddings/${wid}/rsvp/send`, { resend_all: sendModal.resend_all });
      toast.success(`${data.sent} e-mail(s) envoyé(s)${data.failed ? `, ${data.failed} échec(s)` : ""}`);
      setSendModal(null); load();
    } finally { setBusy(false); }
  };
  const openRemind = async () => {
    const { data } = await api.get(`/weddings/${wid}/rsvp/remind-breakdown`);
    setRemindModal({ breakdown: data });
  };
  const confirmRemind = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/weddings/${wid}/rsvp/remind`);
      toast.success(`${data.sent} relance(s) envoyée(s)${data.failed ? `, ${data.failed} échec(s)` : ""}`);
      setRemindModal(null); load();
    } finally { setBusy(false); }
  };

  const copyLink = (token) => {
    navigator.clipboard.writeText(`${window.location.origin}/rsvp/${token}`);
    toast.success("Lien copié");
  };
  const qrUrl = (token) => `https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=${encodeURIComponent(`${window.location.origin}/rsvp/${token}`)}`;

  if (loading) return <Loader />;
  const responded = guests.filter((g) => ["attending", "declined", "maybe", "responded"].includes(g.rsvp_status)).length;

  return (
    <div>
      <PageHeader eyebrow="RSVP" title="Formulaire de réponse"
        description={`${responded}/${guests.length} invités ont répondu`}
        actions={<>
          <Button variant="outline" onClick={openRemind} data-testid="rsvp-remind"><MailWarning className="w-4 h-4 mr-1" /> Relancer</Button>
          <Button variant="outline" onClick={() => openSend(false)} data-testid="rsvp-send-all"><Send className="w-4 h-4 mr-1" /> Envoyer à tous</Button>
          <Button onClick={publish} data-testid="rsvp-publish" className="rounded-full">Publier</Button>
        </>} />

      <div className="flex gap-2 mb-5">
        {[["config", "Configuration"], ["preview", "Prévisualisation"], ["history", "Historique d'envois"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`rsvp-tab-${k}`}
            className={`px-4 py-1.5 rounded-full text-sm border ${tab === k ? "bg-stone-900 text-white" : "bg-white"}`}>{l}</button>
        ))}
      </div>

      {tab === "preview" && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Label className="text-sm">Aperçu pour :</Label>
            <select value={sampleId} onChange={(e) => { setSampleId(e.target.value); loadPreview(e.target.value); }}
              data-testid="preview-guest-select" className="h-9 rounded-md border px-2 text-sm bg-white">
              <option value="">Exemple générique</option>
              {guests.map((g) => <option key={g.id} value={g.id}>{g.first_name} {g.last_name}</option>)}
            </select>
          </div>
          {!preview ? <Loader /> : (
            <div className="grid lg:grid-cols-2 gap-6">
              <div className="card-lux p-6" data-testid="email-preview">
                <div className="flex items-center gap-2 mb-3"><Eye className="w-4 h-4" /><h3 className="font-serif text-lg">Aperçu de l'e-mail</h3></div>
                <p className="text-xs text-stone-400 mb-3">Voici l'e-mail que vos invités recevront.</p>
                <div className="text-sm text-stone-500 mb-2 border-b pb-2">
                  <div><b>De :</b> {preview.email.from_name}</div>
                  <div><b>Objet :</b> {preview.email.subject}</div>
                </div>
                <div className="border rounded-xl overflow-hidden" dangerouslySetInnerHTML={{ __html: preview.email.html }} />
              </div>
              <div className="card-lux p-6" data-testid="rsvp-page-preview">
                <h3 className="font-serif text-lg mb-1">Aperçu de la page de réponse</h3>
                <p className="text-xs text-stone-400 mb-4">Ce que l'invité verra après avoir cliqué — questions {preview.rsvp_page.published ? "publiées" : "configurées (non publiées)"}.</p>
                <div className="rounded-2xl border border-[#ECE4D6] bg-[#FFFDFA] p-6 text-center">
                  <div className="text-[11px] uppercase tracking-[0.25em] text-champagne">{preview.email.from_name}</div>
                  <div className="w-10 h-px bg-[#EADFC8] mx-auto my-3" />
                  <div className="font-serif text-2xl text-stone-800">{preview.wedding_names || "Votre mariage"}</div>
                  <div className="text-sm text-stone-500 mt-1">{preview.rsvp_page.title}</div>
                  {preview.wedding_date_fr && <div className="text-sm text-stone-500 mt-3">Serez-vous parmi nous le {preview.wedding_date_fr} ?</div>}
                  <div className="grid grid-cols-2 gap-3 mt-4 text-left">
                    <div className="p-3 rounded-xl border border-sage/40 bg-sage/10 text-sm font-medium text-sage">✓ Oui, avec plaisir</div>
                    <div className="p-3 rounded-xl border border-stone-200 text-sm text-stone-500">Non, malheureusement</div>
                  </div>
                  {preview.rsvp_page.questions.length > 0 && (
                    <div className="mt-5 space-y-3 text-left">
                      {preview.rsvp_page.questions.map((q, i) => (
                        <div key={i} className="border-l-2 border-champagne/60 pl-3" data-testid={`preview-q-${i}`}>
                          <div className="text-sm font-medium text-stone-700">{q.label}{q.required && <span className="text-destructive"> *</span>}</div>
                          {(q.options || []).length > 0 && <div className="text-xs text-stone-400">{q.options.join(" · ")}</div>}
                        </div>
                      ))}
                    </div>
                  )}
                  {preview.rsvp_page.deadline && <div className="text-xs text-stone-400 mt-4">Réponse souhaitée avant le {fdateShort(preview.rsvp_page.deadline)}</div>}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {tab === "history" && (
        <div className="card-lux divide-y" data-testid="campaign-history">
          <div className="px-5 py-3 flex items-center gap-2 text-stone-500 text-sm"><History className="w-4 h-4" /> Campagnes d'envoi</div>
          {campaigns.length === 0 && <div className="px-5 py-6 text-sm text-stone-400 text-center">Aucun envoi pour le moment.</div>}
          {campaigns.map((c) => (
            <div key={c.id} className="px-5 py-3 flex items-center justify-between text-sm" data-testid={`campaign-${c.id}`}>
              <div><div className="font-medium">{c.type === "invitation" ? "Invitation initiale" : c.type === "invitation_resend" ? "Renvoi d'invitation" : "Relance RSVP"}</div>
                <div className="text-xs text-stone-400">{fdateShort(c.created_at)} {(c.created_at || "").slice(11, 16)} · par {c.sent_by_name}</div></div>
              <div className="text-right"><div className="font-medium">{c.recipient_count} destinataire(s)</div>
                {c.failed > 0 && <div className="text-xs text-destructive">{c.failed} échec(s)</div>}</div>
            </div>
          ))}
        </div>
      )}

      {tab === "config" && (
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="card-lux p-6 space-y-4">
          <h3 className="font-serif text-lg">Configuration</h3>
          <div><Label>Titre</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1 h-10" /></div>
          <div><Label>Message d'introduction</Label><Textarea value={form.intro} onChange={(e) => setForm({ ...form, intro: e.target.value })} rows={2} className="mt-1" /></div>
          <div><Label>Date limite de réponse</Label><Input type="date" value={form.deadline || ""} onChange={(e) => setForm({ ...form, deadline: e.target.value })} className="mt-1 h-10" /></div>
          <div className="flex items-center justify-between pt-2 border-t">
            <div><div className="font-medium text-sm">Questions personnalisées</div>
              <div className="text-xs text-stone-400">Par défaut, seule la présence est demandée</div></div>
            <Button size="sm" variant="outline" onClick={() => addQ()} data-testid="add-question"><Plus className="w-4 h-4" /></Button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {SUGGESTIONS.map((s) => (
              <button key={s.label} type="button" onClick={() => addQ({ ...s, required: false, enabled: true })}
                className="text-xs px-2.5 py-1 rounded-full border bg-white hover:bg-accent" data-testid="suggest-q">+ {s.label}</button>
            ))}
          </div>
          {form.questions.map((q, i) => (
            <div key={i} className="border rounded-xl p-2.5 space-y-2" data-testid={`q-${i}`}>
              <div className="flex items-center gap-2">
                <Input value={q.label} onChange={(e) => updQ(i, "label", e.target.value)} placeholder="Intitulé de la question" className="h-9" />
                <select value={q.type} onChange={(e) => updQ(i, "type", e.target.value)} className="h-9 rounded-md border px-2 text-sm">
                  <option value="text">Texte</option><option value="textarea">Texte long</option>
                  <option value="yesno">Oui/Non</option><option value="choice">Choix</option>
                  <option value="meal">Menu</option><option value="allergies">Allergies</option>
                  <option value="number">Nombre</option></select>
                <button onClick={() => delQ(i)} className="text-stone-300 hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
              </div>
              {(q.type === "choice" || q.type === "meal") && (
                <Input value={(q.options || []).join(", ")} onChange={(e) => updQ(i, "options", e.target.value.split(",").map((x) => x.trim()).filter(Boolean))}
                  placeholder="Options séparées par des virgules" className="h-8 text-xs" data-testid={`q-options-${i}`} />)}
              <label className="flex items-center gap-2 text-xs text-stone-500">
                <input type="checkbox" checked={q.required || false} onChange={(e) => updQ(i, "required", e.target.checked)} /> Obligatoire</label>
            </div>
          ))}
          <Button onClick={save} variant="outline" className="w-full rounded-full">Enregistrer le formulaire</Button>
        </div>

        <div className="card-lux p-6">
          <h3 className="font-serif text-lg mb-4">Liens personnalisés & QR codes</h3>
          <p className="text-sm text-stone-500 mb-4">Chaque foyer dispose d'un lien familial sécurisé unique (une réponse par personne).
            Les invités sans foyer reçoivent un lien individuel. Les réponses mettent à jour automatiquement votre liste et votre plan de table.</p>
          <div className="space-y-2 max-h-[420px] overflow-y-auto">
            {households.map((h) => {
              const members = guests.filter((g) => g.household_id === h.id);
              if (members.length === 0) return null;
              const primary = members[0];
              return (
                <div key={h.id} className="flex items-center gap-3 p-2.5 rounded-xl border bg-accent/20" data-testid={`household-link-${h.id}`}>
                  <img src={qrUrl(primary.rsvp_token)} alt="QR foyer" className="w-12 h-12 rounded" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate flex items-center gap-1"><Users className="w-3.5 h-3.5" /> Foyer {h.name}</div>
                    <div className="text-xs text-stone-400">{members.map((m) => m.first_name).join(", ")}</div>
                  </div>
                  <button onClick={() => copyLink(primary.rsvp_token)} title="Copier le lien familial" className="text-stone-400 hover:text-stone-800"><Copy className="w-4 h-4" /></button>
                  <a href={`/rsvp/${primary.rsvp_token}`} target="_blank" rel="noreferrer" className="text-stone-400 hover:text-stone-800"><ExternalLink className="w-4 h-4" /></a>
                </div>
              );
            })}
            {guests.filter((g) => !g.household_id).map((g) => (
              <div key={g.id} className="flex items-center gap-3 p-2.5 rounded-xl border">
                <img src={qrUrl(g.rsvp_token)} alt="QR" className="w-12 h-12 rounded" />
                <div className="flex-1 min-w-0"><div className="text-sm font-medium truncate">{g.first_name} {g.last_name}</div>
                  <div className="text-xs text-stone-400">{g.invitation_channel === "postal" ? "Postal" : g.invitation_channel === "both" ? "Numérique + Postal" : "Numérique"}</div></div>
                <button onClick={() => copyLink(g.rsvp_token)} title="Copier le lien" className="text-stone-400 hover:text-stone-800"><Copy className="w-4 h-4" /></button>
                <a href={`/rsvp/${g.rsvp_token}`} target="_blank" rel="noreferrer" className="text-stone-400 hover:text-stone-800"><ExternalLink className="w-4 h-4" /></a>
              </div>
            ))}
            {guests.length === 0 && <p className="text-sm text-stone-400 text-center py-6">Ajoutez d'abord des invités.</p>}
          </div>
        </div>
      </div>
      )}

      {/* Send-to-all confirmation */}
      <Dialog open={!!sendModal} onOpenChange={(v) => !v && setSendModal(null)}>
        <DialogContent data-testid="send-confirm-modal"><DialogHeader><DialogTitle>Confirmer l'envoi des invitations</DialogTitle></DialogHeader>
          {sendModal && (
            <div className="space-y-3">
              <p className="text-sm text-stone-600">Vous êtes sur le point d'envoyer l'invitation RSVP par e-mail à <b>{sendModal.resend_all ? (sendModal.breakdown.eligible_unsent + sendModal.breakdown.already_sent) : sendModal.breakdown.eligible_unsent}</b> destinataire(s){!sendModal.resend_all && " qui ne l'ont pas encore reçue"}.</p>
              <div className="text-sm space-y-1 bg-accent/20 rounded-xl p-3">
                <Row label="Recevront l'e-mail" value={sendModal.resend_all ? (sendModal.breakdown.eligible_unsent + sendModal.breakdown.already_sent) : sendModal.breakdown.eligible_unsent} strong />
                {!sendModal.resend_all && <Row label="Déjà reçu — exclus" value={sendModal.breakdown.already_sent} />}
                <Row label="Sans e-mail — exclus" value={sendModal.breakdown.no_email} />
                <Row label="Courrier postal — exclus" value={sendModal.breakdown.postal_only} />
                <Row label="E-mails invalides — exclus" value={sendModal.breakdown.invalid_email} />
              </div>
              {!sendModal.resend_all && sendModal.breakdown.already_sent > 0 && (
                <button onClick={() => setSendModal({ ...sendModal, resend_all: true })} className="text-xs text-champagne underline" data-testid="resend-all-toggle">
                  Renvoyer également aux personnes ayant déjà reçu</button>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendModal(null)}>Annuler</Button>
            <Button onClick={confirmSend} disabled={busy} className="rounded-full" data-testid="send-confirm-btn">
              {busy ? "Envoi…" : `Envoyer ${sendModal ? (sendModal.resend_all ? (sendModal.breakdown.eligible_unsent + sendModal.breakdown.already_sent) : sendModal.breakdown.eligible_unsent) : 0} e-mails`}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reminder confirmation */}
      <Dialog open={!!remindModal} onOpenChange={(v) => !v && setRemindModal(null)}>
        <DialogContent data-testid="remind-confirm-modal"><DialogHeader><DialogTitle>Confirmer la relance</DialogTitle></DialogHeader>
          {remindModal && (
            <div className="space-y-3">
              <p className="text-sm text-stone-600">Vous êtes sur le point d'envoyer une relance à <b>{remindModal.breakdown.pending_eligible}</b> invité(s) qui n'ont pas encore répondu.</p>
              <div className="text-sm space-y-1 bg-accent/20 rounded-xl p-3">
                <Row label="Recevront la relance" value={remindModal.breakdown.pending_eligible} strong />
                <Row label="Déjà présents — exclus" value={remindModal.breakdown.already_attending} />
                <Row label="Déjà déclinés — exclus" value={remindModal.breakdown.already_declined} />
                <Row label="Pas encore invités — exclus" value={remindModal.breakdown.not_yet_invited} />
                <Row label="Sans e-mail — exclus" value={remindModal.breakdown.no_email} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRemindModal(null)}>Annuler</Button>
            <Button onClick={confirmRemind} disabled={busy} className="rounded-full" data-testid="remind-confirm-btn">
              {busy ? "Envoi…" : `Envoyer ${remindModal ? remindModal.breakdown.pending_eligible : 0} relances`}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ label, value, strong }) {
  return (
    <div className="flex justify-between">
      <span className="text-stone-500">{label}</span>
      <span className={strong ? "font-semibold text-stone-900" : "text-stone-600"}>{value}</span>
    </div>
  );
}
